import * as React from "react"
import { Col, Row } from "react-grid-system"
import styled from "styled-components"
import { HorizontalSpacer } from "../../atoms/spacers"
import { useAddTerminalsToGroup } from "../../../hooks/useAddTerminalsToGroup"
import { useSuggestedTerminals } from "../../../hooks/useSuggestedTerminals"
import H3 from "../../atoms/typography/H3"
import Subtitle from "../../atoms/typography/Subtitle"
import { AddTerminalIcon } from "../../atoms/icons/AddTerminalIcon"
import MemberProfileOverview from "../MemberProfileOverview"
import TextInput from "../TextInput"
import { CheckCircleIcon } from "../../atoms/icons/CheckCircleIcon"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { ApolloError } from "@apollo/client"

interface Props {
  groupName: string
  groupId: number | string
  orgId: number | string
}

const AddMemberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: ${(props) => props.theme.borders.softRadius};
  padding: ${(props) => props.theme.spacings.medium};

  :hover {
    background-color: ${(props) => props.theme.colors.greyLight};
  }
`

const IconContainer = styled.div`
  cursor: pointer;

  :hover {
    filter: brightness(60%);
  }
`

const ContentContainer = styled.div`
  max-height: 60vh;
  overflow-y: scroll;
`

const AddTerminalToGroupModal: React.FunctionComponent<Props> = ({ groupName, groupId, orgId }) => {
  const [searchValue, setSearchValue] = React.useState("")

  const { suggestedTerminals } = useSuggestedTerminals(groupId)
  const suggestedWithSearchTerm = useSuggestedTerminals(groupId, searchValue)

  const [invitedTerminals, setInvitedTerminals] = React.useState<Array<{ id: number }>>([])

  const onClickAddSuggestedTerminal = (id: string) => {
    setInvitedTerminals([...invitedTerminals, { id: parseInt(id) }])
    onAddTerminalToGroup({ id: id })
  }

  const checkTerminalIsAdded = (id: string) => {
    const res = invitedTerminals.find((e) => e.id === parseInt(id))
    return res ? true : false
  }

  const onCompletedAddTerminalToGroup = () => {
    console.log("Added terminal to group")
  }
  const onErrorAddedTerminalToGroup = (error: ApolloError) => {
    captureExceptionWithContext(error, "error adding terminal to group")
  }
  const { addTerminalsToGroup } = useAddTerminalsToGroup({
    onCompleted: onCompletedAddTerminalToGroup,
    onError: onErrorAddedTerminalToGroup,
    terminalId: "n/a",
  })
  const onAddTerminalToGroup = async (terminal: { id: string | number }) => {
    try {
      await addTerminalsToGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
          terminalIds: [terminal],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error adding terminal to group")
    }
  }

  return (
    <div style={{ minHeight: "30vh" }}>
      <Row>
        <Col md={6}>
          <Subtitle>Add new terminal to {groupName}</Subtitle>
          <HorizontalSpacer spacing={2} />

          <TextInput
            textFieldTitle="Search terminal"
            placeholder="Terminal name or serial number"
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <HorizontalSpacer spacing={2} />
          <H3>Search results:</H3>
          {searchValue.length > 0 &&
            suggestedWithSearchTerm?.suggestedTerminals?.map((t: any) => {
              return (
                <AddMemberContainer key={t?.serialNumber}>
                  <MemberProfileOverview imgSrc={t?.imageUrl ?? ""} name={t?.nickname} role={t?.serialNumber} />
                  <IconContainer onClick={() => onClickAddSuggestedTerminal(t.id)}>
                    {checkTerminalIsAdded(t.id) ? <CheckCircleIcon /> : <AddTerminalIcon />}
                  </IconContainer>
                </AddMemberContainer>
              )
            })}
        </Col>
        <Col md={6}>
          <H3>Suggested terminals:</H3>
          <HorizontalSpacer spacing={1} />
          <ContentContainer>
            {suggestedTerminals?.map((t: any) => {
              return (
                <AddMemberContainer key={t?.serialNumber}>
                  <MemberProfileOverview imgSrc={t?.imageUrl ?? ""} name={t?.nickname} role={t?.serialNumber} />
                  <IconContainer onClick={() => onClickAddSuggestedTerminal(t.id)}>
                    {checkTerminalIsAdded(t.id) ? <CheckCircleIcon /> : <AddTerminalIcon />}
                  </IconContainer>
                </AddMemberContainer>
              )
            })}
          </ContentContainer>
        </Col>
      </Row>
    </div>
  )
}

export default AddTerminalToGroupModal
